import React, { useState, useCallback } from "react";
import { Radio, Input } from "antd";
import type {
  AssessmentTableProps,
  AssessmentTableRecord,
  AssessmentItemConfig,
  AssessmentListItem,
} from "./types";
import "./index.less";

// ==================== 工具函数 ====================

/**
 * 处理后端返回的数据结构
 * 后端返回格式:
 * - name: 第一层项目名称
 * - childList: 第二层子项列表，每项包含:
 *   - name: 第二层项目名称
 *   - childList: 第三层子项列表，包含:
 *     - name: 实际的评估项名称
 *     - levelList: 等级列表 [{ levelName: "差", description: "..." }]
 */
const convertBackendDataToTableData = (
  items: AssessmentItemConfig[],
  getRemarksText?: (key: string) => string,
): AssessmentTableRecord[] => {
  const records: AssessmentTableRecord[] = [];

  items.forEach((firstLevel) => {
    const firstLevelName = firstLevel.name || firstLevel.label;

    // 处理第二层 childList
    if (firstLevel.childList && firstLevel.childList.length > 0) {
      firstLevel.childList.forEach((secondLevel) => {
        const secondLevelName = secondLevel.name || secondLevel.label;

        // 处理第三层 childList（实际的评估项）
        if (secondLevel.childList && secondLevel.childList.length > 0) {
          secondLevel.childList.forEach((thirdLevel) => {
            const thirdLevelName = thirdLevel.name || thirdLevel.label;
            const key = thirdLevel.key;

            // 从 levelList 提取等级数据
            let levelData: AssessmentListItem[] = [];
            if (thirdLevel.list && thirdLevel.list[0]?.levelList) {
              levelData = thirdLevel.list[0].levelList!.map((level, idx) => ({
                id: idx + 1,
                description: level.description,
                level: level.levelName,
              }));
            }

            records.push({
              key,
              assessmentItem: thirdLevelName,
              list: levelData.length > 0 ? levelData : thirdLevel.list,
              rating: "",
              remarks: getRemarksText
                ? getRemarksText(key)
                : "设定单供应商信息备注...",
            });
          });
        } else {
          // 如果没有第三层，直接使用第二层数据
          const key = secondLevel.key;
          let levelData: AssessmentListItem[] = [];
          if (secondLevel.list && secondLevel.list[0]?.levelList) {
            levelData = secondLevel.list[0].levelList!.map((level, idx) => ({
              id: idx + 1,
              description: level.description,
              level: level.levelName,
            }));
          }

          records.push({
            key,
            assessmentItem: secondLevelName,
            list: levelData.length > 0 ? levelData : secondLevel.list,
            rating: "",
            remarks: getRemarksText
              ? getRemarksText(key)
              : "设定单供应商信息备注...",
          });
        }
      });
    } else {
      // 如果没有 childList，直接使用第一层数据
      const key = firstLevel.key;
      let levelData: AssessmentListItem[] = [];
      if (firstLevel.list && firstLevel.list[0]?.levelList) {
        levelData = firstLevel.list[0].levelList!.map((level, idx) => ({
          id: idx + 1,
          description: level.description,
          level: level.levelName,
        }));
      }

      records.push({
        key,
        assessmentItem: firstLevelName,
        list: levelData.length > 0 ? levelData : firstLevel.list,
        rating: "",
        remarks: getRemarksText
          ? getRemarksText(key)
          : "设定单供应商信息备注...",
      });
    }
  });

  return records;
};

/**
 * 将配置项转换为表格记录
 * @deprecated 使用 convertBackendDataToTableData 替代，以支持新的多层级数据结构
 */
const convertConfigToTableData = (
  items: AssessmentTableProps["items"],
  getRemarksText?: (key: string) => string,
): AssessmentTableRecord[] => {
  return items.map((item) => ({
    key: item.key,
    assessmentItem: item.label,
    list: item.list,
    rating: "",
    remarks: getRemarksText
      ? getRemarksText(item.key)
      : "设定单供应商信息备注...",
  }));
};

// ==================== 主组件 ====================
const AssessmentTable = React.forwardRef<
  {
    getAssessmentData: () => AssessmentTableRecord[];
    getRemarksValues: () => Record<string, string>;
  },
  AssessmentTableProps
>(
  (
    {
      items,
      title = "供应商背景",
      onRatingChange,
      getRemarksText,
      useBackendFormat = false,
      readOnly = false,
    },
    ref,
  ) => {
    // 根据 useBackendFormat 标志选择合适的数据转换函数
    const [data, setData] = useState(() => {
      const result = useBackendFormat
        ? convertBackendDataToTableData(items, getRemarksText)
        : convertConfigToTableData(items, getRemarksText);
      console.log(
        "AssessmentTable 初始化 data:",
        result.map((r) => ({ key: r.key, rating: r.rating })),
      );
      return result;
    });

    // 维护 remarks 输入值的状态
    const [remarksValues, setRemarksValues] = useState<Record<string, string>>(
      {},
    );

    // 处理评级变化
    const handleRatingChange = useCallback(
      (key: string, rating: string) => {
        setData((prevData) =>
          prevData.map((item) =>
            item.key === key ? { ...item, rating } : item,
          ),
        );
        onRatingChange?.(key, rating);
      },
      [onRatingChange],
    );

    // 处理备注输入变化
    const handleRemarksChange = useCallback((key: string, value: string) => {
      console.log(`Remark for ${key} changed to ${value}`);
      setRemarksValues((prev) => ({
        ...prev,
        [key]: value,
      }));
    }, []);

    // 暴露获取评估数据和 remarks 值的方法
    React.useImperativeHandle(ref, () => ({
      getAssessmentData: () => data,
      getRemarksValues: () => remarksValues,
      getAssessmentDataWithRemarks: () => {
        // 返回包含评级和备注信息的完整数据
        return data.map((item) => ({
          ...item,
          remarks: remarksValues[item.key] || "",
          // 同时更新 list 中对应选中项的 remark
          list: item.list.map((listItem) => ({
            ...listItem,
            remark:
              item.rating === listItem.level
                ? remarksValues[item.key] || ""
                : listItem.remark || "",
          })),
        }));
      },
      // 初始化表格的 rating 和 remarks 数据（用于编辑模式回显）
      initializeWithData: (
        selectionMap: Record<string, string>,
        remarksMap: Record<string, string>,
      ) => {
        console.log("AssessmentTable initializeWithData called with:", {
          selectionMap,
          remarksMap,
        });
        // 更新 data 中对应的 rating
        setData((prevData) => {
          const newData = prevData.map((item) => ({
            ...item,
            rating: selectionMap[item.key] || "",
          }));
          console.log("Updated data with ratings:", newData);
          return newData;
        });
        // 更新 remarks 值
        setRemarksValues((prev) => {
          const newRemarks = {
            ...prev,
            ...remarksMap,
          };
          console.log("Updated remarks:", newRemarks);
          return newRemarks;
        });
      },
    }));

    return (
      <div className="assessment-table">
        <div className="assessment-table__title-bar">{title}</div>
        <div className="assessment-table__container">
          {data.map((record) => (
            <div key={record.key} className="assessment-table__row">
              <div className="assessment-table__header-cell">
                {record.assessmentItem}
              </div>
              {readOnly ? (
                // 只读模式：显示所有等级选项（禁用交互），高亮显示被选中的项
                <div style={{ display: "flex", flex: 1 }}>
                  {record.list.length > 0 ? (
                    record.list.map((item) => {
                      const isChecked = item.level === record.rating;
                      return (
                        <div
                          key={`${record.key}-${item.id}`}
                          className={`assessment-table__data-cell ${
                            item.level === "NA"
                              ? "assessment-table__data-cell--na"
                              : ""
                          }`}
                          style={{
                            opacity: isChecked ? 1 : 0.6,
                          }}
                        >
                          <div
                            className={`assessment-table__cell-content ${
                              item.level === "NA"
                                ? "assessment-table__cell-content--na"
                                : ""
                            }`}
                          >
                            <div className="assessment-table__radio-label-container">
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "50%",
                                  border: isChecked
                                    ? "2px solid #1890ff"
                                    : "2px solid #d9d9d9",
                                  backgroundColor: isChecked
                                    ? "#e6f7ff"
                                    : "#fff",
                                  marginRight: "8px",
                                  flexShrink: 0,
                                }}
                              >
                                {isChecked && (
                                  <span
                                    style={{
                                      width: "6px",
                                      height: "6px",
                                      borderRadius: "50%",
                                      backgroundColor: "#1890ff",
                                    }}
                                  />
                                )}
                              </span>
                              <span className="assessment-table__level-text">
                                {item.level}
                              </span>
                            </div>
                            <div className="assessment-table__description-text">
                              {item.description}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        color: "#999",
                        fontSize: "12px",
                      }}
                    >
                      未评估
                    </div>
                  )}
                </div>
              ) : (
                // 编辑模式：显示所有选项的单选按钮
                <Radio.Group
                  value={record.rating}
                  onChange={(e) =>
                    handleRatingChange(record.key, e.target.value)
                  }
                  style={{ display: "flex", flex: 1 }}
                >
                  {record.list.map((item) => (
                    <div
                      key={`${record.key}-${item.id}`}
                      className={`assessment-table__data-cell ${
                        item.level === "NA"
                          ? "assessment-table__data-cell--na"
                          : ""
                      }`}
                    >
                      <div
                        className={`assessment-table__cell-content ${
                          item.level === "NA"
                            ? "assessment-table__cell-content--na"
                            : ""
                        }`}
                      >
                        <div className="assessment-table__radio-label-container">
                          <Radio value={item.level} />
                          <span className="assessment-table__level-text">
                            {item.level}
                          </span>
                        </div>
                        <div className="assessment-table__description-text">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </Radio.Group>
              )}
              <div className="assessment-table__remarks-cell">
                {readOnly ? (
                  // 只读模式：显示纯文本备注
                  <div
                    style={{
                      padding: "4px 8px",
                      borderRadius: "2px",
                      backgroundColor: "#fafafa",
                      minHeight: "32px",
                      display: "flex",
                      alignItems: "center",
                      color: "#666",
                      fontSize: "12px",
                    }}
                  >
                    {remarksValues[record.key] || "-"}
                  </div>
                ) : (
                  // 编辑模式：可编辑的输入框
                  <Input
                    placeholder="请输入备注信息..."
                    allowClear
                    value={remarksValues[record.key] || ""}
                    onChange={(e) =>
                      handleRemarksChange(record.key, e.target.value)
                    }
                    style={{ width: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  },
);

AssessmentTable.displayName = "AssessmentTable";

export default AssessmentTable;

// 导出类型
export type {
  AssessmentListItem,
  AssessmentItemConfig,
  AssessmentTableRecord,
  AssessmentTableProps,
} from "./types";
